const API_BASE = '/api';

// Listeners for global auth events (e.g., redirect to login on 401)
const authListeners = new Set();
export function onUnauthorized(fn) { authListeners.add(fn); return () => authListeners.delete(fn); }
function fireUnauthorized() { authListeners.forEach((fn) => { try { fn(); } catch (e) {} }); }

export async function apiFetch(url, options = {}) {
  // Add cache-busting timestamp to GET requests
  let finalUrl = `${API_BASE}${url}`;
  if (!options.method || options.method === 'GET') {
    const separator = url.includes('?') ? '&' : '?';
    finalUrl += `${separator}_t=${Date.now()}`;
  }

  const res = await fetch(finalUrl, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
  if (res.status === 401) {
    // Only fire global event for protected routes, not the auth check itself
    if (!url.startsWith('/auth/')) fireUnauthorized();
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    const e = new Error(err.error || 'Request failed');
    e.code = err.code;        // e.g. NOT_REVIEWED / IS_CLIENT / ALREADY_MAILED
    e.status = res.status;
    e.body = err;             // full payload (e.g. DUPLICATE carries .existing)
    throw e;
  }
  return res.json();
}

// Auth API
export function loginUser(email, password) {
  return apiFetch('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
}
export function logoutUser() {
  return apiFetch('/auth/logout', { method: 'POST', body: '{}' });
}
export function fetchCurrentUser() { return apiFetch('/auth/me'); }
export function changePassword(currentPassword, newPassword) {
  return apiFetch('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
}

export function globalSearch(q, state = null) {
  const stateParam = state ? `&state=${state}` : '';
  return apiFetch(`/dashboard/search?q=${encodeURIComponent(q)}${stateParam}`);
}

export function fetchLettings(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/lettings?${qs}`);
}
export function fetchLetting(id) { return apiFetch(`/lettings/${id}`); }
export function rescrapeLetting(id) {
  return apiFetch(`/lettings/${id}/rescrape`, { method: 'POST', body: '{}' });
}
// Producer workspace — South Florida bid results. Territory is enforced
// server-side; these params can only narrow, never widen.
export function fetchProducerBidResults(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/producer/bid-results${qs ? `?${qs}` : ''}`);
}
export function fetchProducerLetting(id) { return apiFetch(`/producer/lettings/${id}`); }

// Bid Results — national, every source. Unlike the producer endpoints above,
// nothing here is territory-scoped; the filters are the only narrowing.
export function fetchBidResults(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/bid-results${qs ? `?${qs}` : ''}`);
}
export function fetchBidResultBidders(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/bid-results/bidders${qs ? `?${qs}` : ''}`);
}
// Filter options derived from the data, so a newly-added scraper's source
// appears here the moment it writes its first letting.
export function fetchBidResultFacets(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/bid-results/facets${qs ? `?${qs}` : ''}`);
}

export function fetchFilterOptions(state = null) {
  const qs = state ? `?state=${state}` : '';
  return apiFetch(`/lettings/filters/options${qs}`);
}

export function fetchContractors(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/contractors?${qs}`);
}
export function fetchContractor(id) { return apiFetch(`/contractors/${id}`); }
// Create an individual lead by hand (Google lead, referral, walk-in).
// 409 with e.code === 'DUPLICATE' and e.existing when the name already exists.
export function createContractor(data) {
  return apiFetch('/contractors', { method: 'POST', body: JSON.stringify(data) });
}
export function fetchContractorMap(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/contractors/map${qs ? `?${qs}` : ''}`);
}
export function updateContractor(id, data) {
  return apiFetch(`/contractors/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
// One-shot enrichment: runs state scrapers (Sunbiz/Texas SOS/etc) + finds
// website if missing + scrapes website for contact info. Returns the
// updated contractor row and a list of fields that were filled in.
export function findContractorInfo(id) {
  return apiFetch(`/contractors/${id}/enrich`, { method: 'POST', body: '{}' });
}
export function addTag(id, tag) {
  return apiFetch(`/contractors/${id}/tags`, { method: 'POST', body: JSON.stringify({ tag }) });
}
export function removeTag(id, tag) {
  return apiFetch(`/contractors/${id}/tags/${encodeURIComponent(tag)}`, { method: 'DELETE' });
}

// Analytics API
export function fetchNewContractorAlerts(days = 7) {
  return apiFetch(`/analytics/new-contractor-alerts?days=${days}`);
}

export function createOutreach(data) {
  return apiFetch('/outreach', { method: 'POST', body: JSON.stringify(data) });
}
export function updateOutreach(id, data) {
  return apiFetch(`/outreach/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export function deleteOutreach(id) {
  return apiFetch(`/outreach/${id}`, { method: 'DELETE' });
}
export function generateEmailDraft(contractorId, opts = {}) {
  return apiFetch(`/outreach/email-draft/${contractorId}`, { method: 'POST', body: JSON.stringify(opts) });
}

export function fetchNewContractorsReport(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/reports/new-contractors?${qs}`);
}
export function fetchContractorActivityReport() { return apiFetch('/reports/contractor-activity'); }
export function fetchMarketAnalysis() { return apiFetch('/reports/market-analysis'); }
// Outcome reports — "is the program working?"
export function fetchLeadSourceReport() { return apiFetch('/reports/lead-sources'); }
export function fetchMailerConversion() { return apiFetch('/reports/mailer-conversion'); }
export function fetchPipelineFunnel() { return apiFetch('/reports/pipeline-funnel'); }
export function fetchMarketOpportunities() { return apiFetch('/analytics/market-opportunities'); }
// Per-contractor intelligence (analytics endpoints)
export function fetchBidPatterns(id) { return apiFetch(`/analytics/bid-patterns/${id}`); }
export function fetchCompetitiveIntelligence(id) { return apiFetch(`/analytics/competitive-intelligence/${id}`); }
export function fetchContractorPerformance(id) { return apiFetch(`/analytics/contractor-performance/${id}`); }

export function exportReport(type) {
  window.open(`${API_BASE}/reports/export/${type}`, '_blank');
}

/**
 * Upload one or more Dodge planholder/bidder CSVs.
 * Returns { results: [{ filename, projectId, lettingId, summary } | { filename, error }] }
 */
export async function uploadDodgeCSVs(files) {
  const fd = new FormData();
  for (const f of files) fd.append('files', f, f.name);
  const res = await fetch(`${API_BASE}/dodge-import/upload`, {
    method: 'POST',
    body: fd,
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(err.error || 'Upload failed');
  }
  return res.json();
}

export function fetchContractorContacts(id) {
  return apiFetch(`/contractors/${id}/contacts`);
}

export function fetchDodgeImports() {
  return apiFetch('/dodge-import/imports');
}
export function deleteDodgeImport(lettingId) {
  return apiFetch(`/dodge-import/letting/${lettingId}`, { method: 'DELETE' });
}
export function deleteAllDodgeImports() {
  return apiFetch('/dodge-import/all', { method: 'DELETE' });
}

/**
 * Upload one or more Dodge bid-result XLSX files. Each file becomes an
 * import_batches row attributed to the current user. Returns:
 *   { results: [{ filename, batchId, summary } | { filename, error }] }
 */
export async function uploadExcelBids(files) {
  const fd = new FormData();
  for (const f of files) fd.append('files', f, f.name);
  const totalSizeMb = Array.from(files).reduce((s, f) => s + (f.size || 0), 0) / 1024 / 1024;

  let res;
  try {
    res = await fetch(`${API_BASE}/excel-import/upload`, {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
    });
  } catch (e) {
    // Network-layer failure (the browser never saw a response). Most common
    // cause is the server dropping the connection on a file-size or
    // gateway-timeout limit. Give the user something useful.
    throw new Error(
      `Network error before the server replied (${e.message || 'Failed to fetch'}). ` +
      `Uploaded ${totalSizeMb.toFixed(1)} MB across ${files.length} file${files.length === 1 ? '' : 's'}. ` +
      `If the file is over 100 MB it'll be rejected — split it and try again. ` +
      `Otherwise check the "Past Excel bid-result batches" table below — the import may have actually succeeded.`
    );
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `Upload failed (HTTP ${res.status})` }));
    throw new Error(err.error || `Upload failed (HTTP ${res.status})`);
  }
  return res.json();
}

export function fetchImportBatches() {
  return apiFetch('/excel-import/batches');
}
export function deleteImportBatch(batchId) {
  return apiFetch(`/excel-import/batch/${batchId}`, { method: 'DELETE' });
}

/**
 * Upload XLSX bid-result files for BACKGROUND processing (large files). Returns
 * immediately with { batches: [{ batchId, filename } | { filename, error }] };
 * poll fetchImportJobStatus(batchId) for progress.
 */
export async function uploadExcelBidsAsync(files) {
  const fd = new FormData();
  for (const f of files) fd.append('files', f, f.name);
  const totalSizeMb = Array.from(files).reduce((s, f) => s + (f.size || 0), 0) / 1024 / 1024;
  let res;
  try {
    res = await fetch(`${API_BASE}/excel-import/upload-async`, {
      method: 'POST', body: fd, credentials: 'same-origin',
    });
  } catch (e) {
    throw new Error(
      `Network error before the server replied (${e.message || 'Failed to fetch'}). ` +
      `Uploaded ${totalSizeMb.toFixed(1)} MB across ${files.length} file${files.length === 1 ? '' : 's'}. ` +
      `If a file is over 100 MB it'll be rejected — split it and try again.`
    );
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `Upload failed (HTTP ${res.status})` }));
    throw new Error(err.error || `Upload failed (HTTP ${res.status})`);
  }
  return res.json();
}

export function fetchImportJobStatus(batchId) {
  return apiFetch(`/excel-import/job-status/${batchId}`);
}

// Enrichment APIs.
// enrichContractor() and findContractorInfo() both hit the same endpoint —
// the comprehensive flow that runs state-specific scrapers + searches for
// the company's website + Playwright-scrapes that website for contact info.
// Use either; they're identical.
export function enrichContractor(contractorId) {
  return apiFetch(`/contractors/${contractorId}/enrich`, { method: 'POST', body: '{}' });
}

// Bond Forms (BondFill integration)
export function fetchBondForms() { return apiFetch('/bond-forms'); }
export function fetchBondForm(id) { return apiFetch(`/bond-forms/${id}`); }
export function createBondForm(data) {
  return apiFetch('/bond-forms', { method: 'POST', body: JSON.stringify(data) });
}
export function updateBondForm(id, data) {
  return apiFetch(`/bond-forms/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}
export function deleteBondForm(id) {
  return apiFetch(`/bond-forms/${id}`, { method: 'DELETE' });
}
export async function extractBondFormPdf(file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_BASE}/bond-forms/extract`, { method: 'POST', body: formData });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Extraction failed' }));
    throw new Error(err.error || 'Extraction failed');
  }
  return res.json();
}

// Bond Tools — WIP / Financial / AR extraction
async function uploadAndExtract(path, file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', body: formData });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Extraction failed' }));
    throw new Error(err.error || 'Extraction failed');
  }
  return res.json();
}
export function reviewBondForm(id) {
  return apiFetch(`/bond-forms/${id}/review`, { method: 'POST', body: '{}' });
}
// Document Q&A chat. `messages` is the full conversation so far:
// [{ role: 'user' | 'assistant', content: string }]. Returns { answer }.
export function chatBondForm(id, messages) {
  return apiFetch(`/bond-forms/${id}/chat`, { method: 'POST', body: JSON.stringify({ messages }) });
}
export function extractWipPdf(file) { return uploadAndExtract('/bond-tools/wip/extract', file); }

// Stateless document Q&A for the extraction tools (financial / wip / ar).
// The client holds the extracted `data` and `sourceText` and re-sends them
// with the full conversation each turn. Returns { answer }.
export function chatBondTool({ toolType, data, sourceText, messages }) {
  return apiFetch('/bond-tools/chat', {
    method: 'POST',
    body: JSON.stringify({ toolType, data, sourceText, messages }),
  });
}

// Actionable signals + today
export function fetchSignals(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/dashboard/signals${qs ? '?' + qs : ''}`);
}
export function fetchToday(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/dashboard/today${qs ? '?' + qs : ''}`);
}
export function fetchBidHistory(contractorId) {
  return apiFetch(`/dashboard/bid-history/${contractorId}`);
}

// Admin: database backup — status readout + one-click download.
export function fetchBackupStatus() { return apiFetch('/admin/backup-status'); }
// Streams the DB file as a download. Fetches as a blob (so a failed/HTML
// response doesn't silently "download" an error page) then saves it.
export async function downloadBackup() {
  const res = await fetch(`${API_BASE}/admin/download-db`, { credentials: 'same-origin' });
  if (!res.ok) {
    let msg = `Download failed (${res.status})`;
    try { const j = await res.json(); if (j.error) msg = j.error; } catch (e) { /* not json */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const disp = res.headers.get('content-disposition') || '';
  const m = disp.match(/filename="?([^"]+)"?/);
  const filename = (m && m[1]) || 'bondhq-backup.db';
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
  return { filename, bytes: blob.size };
}

// Admin: bid-schedule junk cleanup. Dry run (preview) by default; pass
// { apply: true } to actually delete.
export function cleanupBidJunk({ apply = false } = {}) {
  return apiFetch(`/admin/cleanup-bid-junk${apply ? '?apply=1' : ''}`, { method: 'POST', body: '{}' });
}

// Admin: contractors sharing a FEIN, and the review actions on them.
export function fetchFeinDuplicates() { return apiFetch('/admin/duplicates/fein'); }
export function mergeContractors({ keepId, mergeId, fein }) {
  return apiFetch('/admin/duplicates/merge', { method: 'POST', body: JSON.stringify({ keep_id: keepId, merge_id: mergeId, fein }) });
}
export function dismissFeinDuplicates({ fein, ids }) {
  return apiFetch('/admin/duplicates/dismiss', { method: 'POST', body: JSON.stringify({ fein, ids }) });
}

// Admin: bulk-repair historical FloridaBids lettings (fills blank bid amounts +
// fixes wrong winners). Runs in the BACKGROUND — returns { started, applied }
// immediately; progress lands in server logs and the letting pages update as it
// goes. Dry run by default; { apply: true } writes.
export function repairFloridaBids({ apply = false } = {}) {
  return apiFetch(`/admin/repair-floridabids${apply ? '?apply=1' : ''}`, { method: 'POST', body: '{}' });
}

// Admin: scraper status + manual triggers
export function fetchScrapers() { return apiFetch('/admin/scrapers'); }
export function fetchScrapeHistory(limit = 50) { return apiFetch(`/admin/scrape-history?limit=${limit}`); }
export function triggerScraperByPath(triggerPath) {
  // triggerPath is the full /api/scraper/trigger-xxx path (without /api prefix since apiFetch adds it)
  const relative = triggerPath.replace(/^\/api/, '');
  return apiFetch(relative, { method: 'POST', body: '{}' });
}

// Pipeline / CRM
export function fetchPipelineStages() { return apiFetch('/pipeline/stages'); }
export function createPipelineStage(stage) { return apiFetch('/pipeline/stages', { method: 'POST', body: JSON.stringify(stage) }); }
export function updatePipelineStage(id, stage) { return apiFetch(`/pipeline/stages/${id}`, { method: 'PUT', body: JSON.stringify(stage) }); }
export function deletePipelineStage(id) { return apiFetch(`/pipeline/stages/${id}`, { method: 'DELETE' }); }
export function fetchPipelineBoard(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/pipeline/board${qs ? '?' + qs : ''}`);
}
export function movePipelineContractor(contractor_id, to_stage_id, note, lost_reason) {
  return apiFetch('/pipeline/move', { method: 'POST', body: JSON.stringify({ contractor_id, to_stage_id, note, lost_reason }) });
}
// opts: { claim: true } — the adding agent owns it even in Lead;
//       { if_absent: true } — 409 IN_PIPELINE instead of moving a card already on the board.
export function addContractorToPipeline(contractor_id, stage_id, opts = {}) {
  return apiFetch(`/pipeline/add/${contractor_id}`, { method: 'POST', body: JSON.stringify({ stage_id, ...opts }) });
}
export function removeContractorFromPipeline(contractor_id) {
  return apiFetch(`/pipeline/remove/${contractor_id}`, { method: 'DELETE' });
}
// Deal fields on a pipeline card: { bond_line, premium, close_date, lost_reason }.
export function updatePipelineDeal(contractor_id, deal) {
  return apiFetch(`/pipeline/deal/${contractor_id}`, { method: 'PUT', body: JSON.stringify(deal) });
}

export function updatePipelineNotes(contractor_id, notes) {
  return apiFetch(`/pipeline/notes/${contractor_id}`, { method: 'PUT', body: JSON.stringify({ notes }) });
}
export function fetchPipelineHistory(contractor_id) {
  return apiFetch(`/pipeline/history/${contractor_id}`);
}
export function fetchFollowUps(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/pipeline/follow-ups${qs ? '?' + qs : ''}`);
}
export function createFollowUp(payload) {
  return apiFetch('/pipeline/follow-ups', { method: 'POST', body: JSON.stringify(payload) });
}
// outcome (optional): { outcome, note, next_due_date } — see OUTCOMES in
// server/routes/pipeline.js. With no outcome it is a bare completion.
export function completeFollowUp(id, outcome) {
  return apiFetch(`/pipeline/follow-ups/${id}/complete`, {
    method: 'PUT',
    ...(outcome ? { body: JSON.stringify(outcome) } : {}),
  });
}
// Log a touch outside a follow-up completion (the prompt after a Call click):
// { contractor_id, outcome, note, next_due_date, kind: 'call', complete_follow_up_id }.
export function logTouch(payload) {
  return apiFetch('/pipeline/touches', { method: 'POST', body: JSON.stringify(payload) });
}
export function updateFollowUp(id, payload) {
  return apiFetch(`/pipeline/follow-ups/${id}`, { method: 'PATCH', body: JSON.stringify(payload) });
}
export function deleteFollowUp(id) { return apiFetch(`/pipeline/follow-ups/${id}`, { method: 'DELETE' }); }
export function extractFinancialPdf(file) { return uploadAndExtract('/bond-tools/financial/extract', file); }
export function extractArPdf(file) { return uploadAndExtract('/bond-tools/ar/extract', file); }

export function enrichContractorsBatch(contractorIds, options = {}) {
  return apiFetch('/enrichment/batch', {
    method: 'POST',
    body: JSON.stringify({ contractor_ids: contractorIds, ...options })
  });
}

export function enrichContractorsAuto(options = {}) {
  return apiFetch('/enrichment/auto', {
    method: 'POST',
    body: JSON.stringify(options)
  });
}

export function fetchEnrichmentStats(state) {
  return apiFetch('/enrichment/stats' + (state ? `?state=${encodeURIComponent(state)}` : ''));
}

// CPP Document Orders API
export function triggerCPPScrape(options = {}) {
  return apiFetch('/cpp/trigger', { method: 'POST', body: JSON.stringify(options) });
}
export function fetchCPPOpportunities(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/cpp/opportunities?${qs}`);
}
export function fetchCPPOpportunitiesSummary() {
  return apiFetch('/cpp/opportunities/summary');
}
export function fetchCPPOrders(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/cpp/orders?${qs}`);
}
export function fetchCPPOrdersByContractor(contractorId) {
  return apiFetch(`/cpp/orders/contractor/${contractorId}`);
}
export function fetchCPPHistory(limit = 10) {
  return apiFetch(`/cpp/history?limit=${limit}`);
}
export function fetchCPPStats() {
  return apiFetch('/cpp/stats');
}
export function refreshCPPOpportunities() {
  return apiFetch('/cpp/refresh-opportunities', { method: 'POST' });
}

// ── Assistant Outreach Workspace ──────────────────────────────────────────
export function fetchOutreachQueue(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/outreach/queue?${qs}`);
}
// status is one of 'none' | 'bond' | 'insurance' | 'both' (a legacy boolean is
// still accepted by the server and mapped to bond/none).
export function reviewClientStatus(contractorId, status) {
  return apiFetch(`/outreach/review/${contractorId}`, {
    method: 'POST',
    body: JSON.stringify({ client_status: status }),
  });
}
export function fetchClientStatuses() { return apiFetch('/outreach/client-statuses'); }
export function sendMailer(payload) {
  return apiFetch('/outreach/mailer', { method: 'POST', body: JSON.stringify(payload) });
}
export function fetchLeaderboard(window = 'today') {
  return apiFetch(`/outreach/leaderboard?window=${window}`);
}
export function fetchPartners() { return apiFetch('/outreach/partners'); }

// ── Stepped mailer letters (contractor profile) ───────────────────────────
export function fetchMailerDraft(contractorId) {
  return apiFetch(`/outreach/mailer-draft/${contractorId}`);
}
export function logMailerLetter(payload) {
  return apiFetch('/outreach/mailer-letter', { method: 'POST', body: JSON.stringify(payload) });
}
// Active users (includes role) — powers the lead-owner selector + letter signers.
export function fetchAuthUsers() { return apiFetch('/auth/users'); }
// Ownership audit trail for a contractor (who → whom, by whom, via what).
export function fetchAssignmentHistory(contractorId) {
  return apiFetch(`/contractors/${contractorId}/assignment-history`);
}
// Own letter identity: { name, title, phone, signature_image }
export function updateMyProfile(fields) {
  return apiFetch('/auth/me/profile', { method: 'PUT', body: JSON.stringify(fields) });
}

// Contractor contacts (assistants add/edit)
export function createContact(contractorId, data) {
  return apiFetch(`/contractors/${contractorId}/contacts`, {
    method: 'POST', body: JSON.stringify(data),
  });
}
export function updateContact(contractorId, contactId, data) {
  return apiFetch(`/contractors/${contractorId}/contacts/${contactId}`, {
    method: 'PATCH', body: JSON.stringify(data),
  });
}
export function deleteContact(contractorId, contactId) {
  return apiFetch(`/contractors/${contractorId}/contacts/${contactId}`, { method: 'DELETE' });
}
export function mergeDuplicateContacts(contractorId) {
  return apiFetch(`/contractors/${contractorId}/contacts/merge-duplicates`, { method: 'POST' });
}

// Team / user management (admin)
export function fetchTeam() { return apiFetch('/admin/users'); }
export function createTeamMember(data) {
  return apiFetch('/admin/users', { method: 'POST', body: JSON.stringify(data) });
}
export function updateTeamMember(id, data) {
  return apiFetch(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}
export function deleteTeamMember(id) {
  return apiFetch(`/admin/users/${id}`, { method: 'DELETE' });
}

// ── Financials on file (WIP / financial statement summaries per contractor) ──
export function attachContractorFinancials({ contractor_id, kind, data, source_filename }) {
  return apiFetch('/contractor-financials', {
    method: 'POST', body: JSON.stringify({ contractor_id, kind, data, source_filename }),
  });
}

export function fetchContractorFinancials(contractorId) {
  return apiFetch(`/contractor-financials/contractor/${contractorId}`);
}

export function deleteContractorFinancial(id) {
  return apiFetch(`/contractor-financials/${id}`, { method: 'DELETE' });
}
